import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Button, Form, Modal, Offcanvas } from 'react-bootstrap'
import { Formik, FormikHelpers, useFormikContext } from 'formik'
import { useFormikReset, FormikResetProvider } from '../CommonComponents'
import { Article, Layoutable } from '../types'

// --------------------------------------------------------------------------------
// new editting offcanvas
// --------------------------------------------------------------------------------

type OnHideHandle = { onHide: () => void }

interface OnHideHandlerProps {
  onDone: (e?: unknown) => void
  setCloseModalShowing: React.Dispatch<React.SetStateAction<boolean>>
}

export const OnHideHandler = forwardRef<OnHideHandle, OnHideHandlerProps>(({ onDone, setCloseModalShowing }, ref) => {
  const context = useFormikContext()

  useImperativeHandle(ref, () => ({
    onHide: () => {
      if (!context.dirty) {
        onDone(undefined)
        return
      }
      setCloseModalShowing(true)
    }
  }))

  return undefined
})

interface ArticleDetailOperationButtonsProps {
  onDone: (e?: unknown) => void
  onSave: (e: unknown, context: unknown) => void
  setCloseModalShowing: React.Dispatch<React.SetStateAction<boolean>>
  inputDisabled: boolean | undefined
  transitioning: boolean
}

export const ArticleDetailOperationButtons: React.FC<ArticleDetailOperationButtonsProps> = ({
  onDone,
  onSave,
  setCloseModalShowing,
  inputDisabled,
  transitioning
}) => {
  const context = useFormikContext()
  const formikReset = useFormikReset()

  const onCloseClick = (e: React.MouseEvent) => {
    if (context.isSubmitting) {
      return
    }

    if (!context.dirty) {
      onDone(e)
      return
    }
    setCloseModalShowing(true)
  }
  const onRevertClick = (_e: React.MouseEvent) => {
    if (context.isSubmitting) {
      return
    }

    formikReset.handleReset()
    setTimeout(() => {
      context.validateForm()
    }, 0)
  }
  const onSaveClick = (e: React.MouseEvent) => {
    if (context.isSubmitting) {
      return
    }

    context.submitForm()
    onSave(e, context)
  }

  return (
    <div className='d-flex justify-content-end p-1 me-2 articleDetailOperationButtons'>
      <div className='p-1'>
        <Button
          size='sm'
          className='closeButton'
          variant='outline-primary'
          onClick={onCloseClick}
          disabled={transitioning}
        >
          閉じる
        </Button>
      </div>
      <div className='p-1'>
        <Button
          size='sm'
          className='revertButton'
          variant='outline-primary'
          onClick={onRevertClick}
          disabled={!context.dirty || inputDisabled || transitioning}
        >
          元に戻す
        </Button>
      </div>
      <div className='p-1'>
        <Button
          size='sm'
          className='submitButton'
          onClick={onSaveClick}
          disabled={!context.dirty || inputDisabled || transitioning}
        >
          保存
        </Button>
      </div>
    </div>
  )
}

interface CloseModalProps {
  show: boolean
  setShow: React.Dispatch<React.SetStateAction<boolean>>
  onReturnClick?: (e?: unknown) => void
  onDiscardClick?: (e?: unknown) => void
}

const CloseModal: React.FC<CloseModalProps> = ({ show, setShow, onReturnClick, onDiscardClick }) => {
  const context = useFormikContext()

  const onReturnClickProxy = (e: React.MouseEvent) => {
    setShow(false)
    if (onReturnClick) {
      onReturnClick(e)
    }
  }
  const onDiscardClickProxy = (e: React.MouseEvent) => {
    setShow(false)
    if (onDiscardClick) {
      onDiscardClick(e)
    }
  }
  const onSaveAndCloseClickProxy = (_e: React.MouseEvent) => {
    context.submitForm()
    setShow(false)
  }

  return (
    <Modal show={show} backdrop={true} onHide={onReturnClick}>
      <Modal.Header>
        <Modal.Title />
      </Modal.Header>
      <Modal.Body>設定の変更が保存されていません。このまま閉じますか？</Modal.Body>
      <Modal.Footer>
        <div className='p-1'>
          <Button
            size='sm'
            variant='outline-primary'
            className='detail-offcanvas-close-modal-return-button'
            onClick={onReturnClickProxy}
          >
            戻る
          </Button>
        </div>
        <div className='p-1'>
          <Button
            size='sm'
            className='detail-offcanvas-close-modal-discard-button'
            variant='outline-danger'
            onClick={onDiscardClickProxy}
          >
            保存せず閉じる
          </Button>
        </div>
        <div className='p-1'>
          <Button
            size='sm'
            className='detail-offcanvas-close-modal-save-button'
            variant='primary'
            onClick={onSaveAndCloseClickProxy}
          >
            保存して閉じる
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

interface Cell {
  column: string
  rowId: number
  articleId?: number
}

type DetailEventHandler = {
  onSubmit: (values: unknown, actions?: unknown) => Promise<unknown>
}

interface DetailOffcanvasProps {
  children: React.ReactNode
  show: boolean
  articles?: Article[]
  cell?: Cell
  layoutable: Layoutable
  layoutableId: string | number
  layoutableType: string
  holderArticle?: Article
  enumValues: unknown
  baseUri: string
  isSelectedLayoutableWritable: boolean
  childrenArticles: Record<string, Article[]>
  onDone: (e?: unknown) => void
}

export const DetailOffcanvas: React.FC<DetailOffcanvasProps> = ({
  children,
  show,
  articles,
  cell,
  layoutable,
  layoutableId,
  layoutableType,
  holderArticle,
  enumValues,
  baseUri,
  isSelectedLayoutableWritable,
  childrenArticles,
  onDone
}) => {
  const [closeModalShowing, setCloseModalShowing] = useState(false)
  const [transitioning, setTransitioning] = useState(false)
  const handleModalReturn = (_e?: unknown) => {}
  const handleModalDiscard = (e?: unknown) => {
    onDone(e)
  }

  const eventHandlerRef = useRef<DetailEventHandler | null>(null)
  const handleSubmit = (values: unknown, actions: FormikHelpers<unknown>) => {
    if (eventHandlerRef.current !== null) {
      return eventHandlerRef.current
        .onSubmit(values, actions)
        .then((_result) => {
          console.log('submit success')
        })
        .catch((err) => {
          console.error('submit failed:', err)
        })
        .finally(() => {
          actions.setSubmitting(false)
          onDone(undefined)
        })
    }
    actions.setSubmitting(false)
  }

  const onHideHandlerRef = useRef<OnHideHandle | null>(null)
  const onHide = () => {
    onHideHandlerRef.current?.onHide()
  }
  const onSave = (_e: unknown, _formikContext: unknown) => {
    // save handling
  }

  const childrenArray = React.Children.toArray(children) as React.ReactElement<Record<string, unknown>>[]
  const activeChild = cell && childrenArray.find((child) => child.props.type === cell.column)
  const article = cell && articles?.at(cell.rowId)

  const inputDisabled = !isSelectedLayoutableWritable || !!article?.layoutLocked
  const propsToChild = {
    onDone,
    article,
    layoutable,
    layoutableId,
    layoutableType,
    holderArticle,
    enumValues,
    baseUri,
    isSelectedLayoutableWritable,
    inputDisabled,
    childrenArticles,
    eventHandlerRef
  }

  const updatedChild = activeChild && React.cloneElement(activeChild, { ...activeChild.props, ...propsToChild })

  const DebugLog = (_props: Record<string, never>) => {
    const context = useFormikContext()
    useEffect(() => {
      if (process.env.NODE_ENV === 'development') {
        console.group(
          '%c Form State Updated',
          'background: #1e88e5; color: white; padding: 1px 3px; border-radius: 2px;'
        )
        console.log('%c Initial:', 'color: #5E5EDD; font-weight: bold;', context.initialValues)
        console.log('%c Current:', 'color: #5EDD5E; font-weight: bold;', context.values)
        console.log('%c Errors:', 'color: #DD5E5E; font-weight: bold;', context.errors)
        console.groupEnd()
      }
    }, [context])
    return undefined
  }

  const buttonsProps: ArticleDetailOperationButtonsProps = {
    onDone,
    onSave,
    setCloseModalShowing,
    inputDisabled,
    transitioning
  }

  const [articleUpdate, setArticleUpdate] = useState(0)
  const articleKey = cell ? `${article?.id ?? cell.rowId}:${cell.column}` : null
  useEffect(() => {
    setArticleUpdate((prev) => prev + 1)
  }, [articleKey])

  if (updatedChild === undefined) {
    return undefined
  }

  return (
    <Offcanvas
      show={show}
      className='articleDetailOffcanvas'
      onHide={onHide}
      onEntering={() => setTransitioning(true)}
      onEntered={() => setTransitioning(false)}
      onExiting={() => setTransitioning(true)}
      onExited={() => setTransitioning(false)}
      placement='end'
      backdrop
      style={{ width: '600px' }}
    >
      <Formik key={articleUpdate} initialValues={{}} onSubmit={handleSubmit}>
        <FormikResetProvider>
          <Form>
            <DebugLog />
            <div className='d-flex flex-column vh-100'>
              {updatedChild}
              <div>
                <ArticleDetailOperationButtons {...buttonsProps} />
              </div>
            </div>
            <OnHideHandler ref={onHideHandlerRef} {...{ onDone, setCloseModalShowing }} />
            <CloseModal
              show={closeModalShowing}
              setShow={setCloseModalShowing}
              onReturnClick={handleModalReturn}
              onDiscardClick={handleModalDiscard}
            />
          </Form>
        </FormikResetProvider>
      </Formik>
    </Offcanvas>
  )
}
